import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, Trash2, Globe, ShieldAlert, UserCheck, KeyRound } from "lucide-react";

const KIND_LABEL = {
  guest_login: { label: "Guest entered", icon: UserCheck, tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  guest_login_failed: { label: "Bad password", icon: ShieldAlert, tone: "text-destructive/85 bg-destructive/5 border-destructive/30" },
  admin_login: { label: "Admin login", icon: KeyRound, tone: "text-accent bg-accent/10 border-accent/30" },
};

const uaShort = (ua) => {
  if (!ua) return "";
  // Best-effort short browser + OS extraction
  let os = "";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
  else if (/iPhone|iPad|iOS/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Linux/i.test(ua)) os = "Linux";
  let br = "";
  if (/Firefox\//i.test(ua)) br = "Firefox";
  else if (/Edg\//i.test(ua)) br = "Edge";
  else if (/OPR\//i.test(ua)) br = "Opera";
  else if (/Chrome\//i.test(ua)) br = "Chrome";
  else if (/Safari\//i.test(ua)) br = "Safari";
  else if (/curl\//i.test(ua)) br = "curl";
  return [br, os].filter(Boolean).join(" · ") || ua.slice(0, 30);
};

const fmtTs = (ts) => {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const StatCard = ({ label, value, sublabel }) => (
  <div className="surface-card p-5" data-testid={`admin-visits-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <p className="editorial-kicker">{label}</p>
    <p className="mt-2 font-serif-display text-4xl tabular-nums">{value}</p>
    {sublabel && <p className="mt-1 text-[11px] text-foreground/50 tracking-widest uppercase">{sublabel}</p>}
  </div>
);

export const AdminVisits = () => {
  const [data, setData] = useState({ visits: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/visits?limit=1000");
      setData({ visits: data.visits || [], summary: data.summary || null });
    } catch (_e) {
      toast.error("Failed to load visits.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (kindFilter === "all") return data.visits;
    return data.visits.filter((v) => v.kind === kindFilter);
  }, [data.visits, kindFilter]);

  const onClear = async () => {
    if (!window.confirm("Permanently clear the visitor log? This cannot be undone.")) return;
    try {
      await api.delete("/admin/visits");
      toast.success("Visitor log cleared.");
      await load();
    } catch (_e) {
      toast.error("Failed to clear log.");
    }
  };

  const s = data.summary;

  return (
    <div className="space-y-8" data-testid="admin-visits-page">
      {/* Header + summary */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="editorial-kicker">Visitor log</p>
          <p className="text-sm text-foreground/60 mt-1 max-w-[62ch]">
            Every successful password entry, failed attempt, and admin login is logged with the
            visitor's IP, approximate location, browser, and time. Location is best-effort via
            free IP geolocation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" className="h-10 px-4 rounded-xl text-[12px] tracking-[0.18em] uppercase" data-testid="admin-visits-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={onClear} variant="outline" className="h-10 px-4 rounded-xl text-[12px] tracking-[0.18em] uppercase text-destructive/85 border-destructive/40 hover:bg-destructive/10 hover:text-destructive" data-testid="admin-visits-clear">
            <Trash2 className="w-4 h-4 mr-2" /> Clear log
          </Button>
        </div>
      </div>

      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total visits" value={s.total} />
            <StatCard label="Unique IPs" value={s.unique_ips} />
            <StatCard label="Last 24h" value={s.last_24h} />
            <StatCard label="Last 7d" value={s.last_7d} />
          </div>

          {s.top_countries && s.top_countries.length > 0 && (
            <div className="surface-card p-5" data-testid="admin-visits-top-countries">
              <p className="editorial-kicker mb-3">
                <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> Top countries
              </p>
              <ul className="flex flex-wrap gap-2">
                {s.top_countries.map((c) => (
                  <li key={c.country_code} className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-foreground/5 text-sm">
                    <span className="text-lg leading-none" aria-hidden="true">{c.flag || "🏳️"}</span>
                    <span className="font-medium">{c.country}</span>
                    <span className="text-foreground/50 tabular-nums text-xs">({c.count})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: "All" },
          { key: "guest_login", label: "Guests entered" },
          { key: "guest_login_failed", label: "Bad password attempts" },
          { key: "admin_login", label: "Admin logins" },
        ].map((f) => {
          const active = kindFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setKindFilter(f.key)}
              data-testid={`admin-visits-filter-${f.key}`}
              className={
                "h-8 px-3 rounded-full text-[11px] tracking-[0.18em] uppercase transition-colors " +
                (active ? "bg-foreground text-background" : "border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60")
              }
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="surface-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center editorial-kicker">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center editorial-kicker">No visits recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-visits-table">
              <thead>
                <tr className="bg-foreground/5 text-[10px] tracking-[0.22em] uppercase text-foreground/60">
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Event</th>
                  <th className="text-left px-4 py-3">Location</th>
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-left px-4 py-3">Device</th>
                  <th className="text-left px-4 py-3">Lang</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const meta = KIND_LABEL[v.kind] || { label: v.kind || "unknown", icon: Globe, tone: "text-foreground/60 bg-foreground/5 border-border" };
                  const Icon = meta.icon;
                  return (
                    <tr key={v.id} className="border-t border-border/60 hover:bg-foreground/[0.02]" data-testid={`admin-visits-row-${v.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground/70">{fmtTs(v.ts)}</td>
                      <td className="px-4 py-3">
                        <span className={"inline-flex items-center gap-1.5 h-6 px-2 rounded-full border text-[10px] tracking-[0.14em] uppercase " + meta.tone}>
                          <Icon className="w-3 h-3" />{meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {v.flag || v.country ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="text-lg leading-none" aria-hidden="true">{v.flag || "🏳️"}</span>
                            <span>
                              <span className="font-medium">{v.country || v.country_code || "—"}</span>
                              {v.city && <span className="text-foreground/55"> · {v.city}</span>}
                            </span>
                          </span>
                        ) : (
                          <span className="text-foreground/40">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground/75 whitespace-nowrap">{v.ip || "—"}</td>
                      <td className="px-4 py-3 text-xs text-foreground/70" title={v.user_agent}>{uaShort(v.user_agent)}</td>
                      <td className="px-4 py-3 text-xs text-foreground/60 uppercase">{(v.language || "").slice(0, 5)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVisits;
